import React, { useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  AlertCircle,
  CalendarDays,
  Edit,
  FileText,
  FolderOpen,
  Plus,
  Scale,
  Search,
  Shield,
  Trash,
} from "lucide-react";

import { isoDateToMDY } from "../../../utils/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";
import { AddBookingModal } from "./modals/AddBooking";
import { AddCaseModal } from "./modals/AddCase";
import { AddHearingModal } from "./modals/AddHearing";
import { DeleteBookingModal } from "./modals/DeleteBooking";
import { DeleteCaseModal } from "./modals/DeleteCase";
import { DeleteHearingModal } from "./modals/DeleteHearing";
import { EditBookingModal } from "./modals/EditBooking";
import { EditCaseModal } from "./modals/EditCase";
import { EditHearingModal } from "./modals/EditHearing";
import { EditLegalGeneral } from "./modals/EditLegalGeneral";

type ParticipantHearing = {
  id?: string | number | null;
  participant?: string | number | null;
  hearingDate?: string | null;
  hearingID?: string | null;
  caseNumber?: string | null;
  caseNumbers?: string | null;
  details?: string | null;
  type?: string | null;
  program?: string | null;
  courtOrders?: string[] | null;
  courtOther?: string | null;
  minutes?: string | null;
  reinstatementNumber?: string | null;
};

type ParticipantCharge = {
  charge?: string | null;
  charge_entrydate?: string | null;
};

type ParticipantCase = {
  id?: string | number | null;
  participant?: string | number | null;
  caseNumber?: string | null;
  program?: string | null;
  entryDate?: string | null;
  bookingNumber?: string | null;
  caseOpen?: boolean | string | number | null;
  caseOutcome?: string | null;
  hideFromQuarterly?: boolean | string | number | null;
  hearings?: ParticipantHearing[] | null;
  charges?: ParticipantCharge[] | null;
};

type ParticipantBooking = {
  participant?: string | number | null;
  program?: string | null;
  currentNumber?: string | number | null;
  booking?: string | null;
  bookingNumber?: string | null;
  diversionDate?: string | null;
  releaseDate?: string | null;
  arrestDate?: string | null;
  location?: string | null;
  race?: string | null;
  cases?: ParticipantCase[] | null;
};

type ParticipantLegalData = {
  bookingDetails?: ParticipantBooking[] | null;
  hearings?: ParticipantHearing[] | null;
  bookingNumber?: string | null;
  arrestDate?: string | null;
  istStatus?: { status?: string | null; statusDate?: string | null } | null;
  maxDate?: string | null;
  rso?: string | null;
  mainNumber?: string | null;
  commitmentDate?: string | null;
  completionDate?: string | null;
  dischargeDate?: string | null;
  diversionDate?: string | null;
  releasedDate?: string | null;
};

type BookingView = {
  participant: string | null;
  program: string | null;
  currentNumber: number;
  bookingNumber: string;
  arrestDate: string | null;
  releaseDate: string | null;
  location: string | null;
  race: string | null;
  diversionDate: string | null;
  cases: CaseView[];
};

type CaseView = {
  participant: string | null;
  caseNumber: string;
  entryDate: string | null;
  program: string | null;
  caseOpen: boolean;
  caseOutcome: string | null;
  hideFromQuarterly: boolean;
  charges: ParticipantCharge[];
  hearings: HearingView[];
  rawCase: ParticipantCase;
};

type HearingView = {
  participant: string | null;
  hearingDate: string | null;
  hearingID: string;
  caseNumber: string | null;
  caseNumbers: string | null;
  details: string | null;
  type: string | null;
  minutes: string | null;
  reinstatementNumber: string | null;
  courtOrders: string[];
  rawHearing: ParticipantHearing;
};

const GET_LEGAL_STATUS_DATA = gql`
  query GetLegalStatusData($odrnumber: Int!, $participant: Int) {
    getParticipantByODRNumber(odrnumber: $odrnumber, participant: $participant) {
      bookingDetails {
        participant
        program
        currentNumber
        booking
        bookingNumber
        diversionDate
        releaseDate
        arrestDate
        location
        race
        cases {
          id
          participant
          caseNumber
          program
          entryDate
          bookingNumber
          caseOpen
          caseOutcome
          hideFromQuarterly
          hearings {
            id
            participant
            hearingDate
            hearingID
            caseNumber
            caseNumbers
            details
            type
            program
            courtOrders
            courtOther
            minutes
            reinstatementNumber
          }
          charges {
            charge
            charge_entrydate
          }
        }
      }
      hearings {
        id
        participant
        hearingDate
        hearingID
        caseNumber
        caseNumbers
        details
        type
        program
        courtOrders
        courtOther
        minutes
        reinstatementNumber
      }
      bookingNumber
      arrestDate
      istStatus {
        status
        statusDate
      }
      maxDate
      rso
      mainNumber
      commitmentDate
      completionDate
      dischargeDate
      diversionDate
      releasedDate
    }
  }
`;

const sectionCardClassName = "participant-card overflow-hidden shadow-[var(--shadow-light)]";
const iconButtonClassName = "h-8 w-8 border-[var(--neutral-gray-30)] px-0 text-[var(--neutral-gray-80)] hover:bg-[var(--surface-muted)] hover:text-[var(--azure-primary)]";
const inlineActionClassName = "h-8 border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)] px-2.5 text-xs font-medium text-[var(--azure-primary)] hover:bg-[var(--azure-primary-soft)] hover:text-[var(--azure-primary-hover)]";

const normalizeRso = (value: unknown) => {
  const normalizedValue = String(value ?? "").trim().toLowerCase();
  if (!normalizedValue) {
    return false;
  }
  return ["1", "true", "yes", "y", "rso"].includes(normalizedValue);
};

const normalizeDateValue = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) {
    return null;
  }

  if (/^\d+$/.test(normalizedValue)) {
    const fromTimestamp = new Date(Number(normalizedValue));
    if (!Number.isNaN(fromTimestamp.getTime())) {
      return fromTimestamp.toISOString();
    }
  }

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString();
  }

  return normalizedValue;
};

const formatDate = (value: string | null | undefined) => {
  const normalizedDate = normalizeDateValue(value);
  if (!normalizedDate) {
    return "—";
  }

  return isoDateToMDY(normalizedDate);
};

const getMinutesHref = (minutes: string | null | undefined) => {
  const normalizedMinutes = String(minutes || "").trim();
  if (!normalizedMinutes) {
    return null;
  }

  if (normalizedMinutes.startsWith("http://") || normalizedMinutes.startsWith("https://")) {
    return normalizedMinutes;
  }

  return `/${normalizedMinutes.replace(/^\/+/, "")}`;
};

const formatHearingDetails = (hearing: HearingView) => {
  const details = String(hearing.details || "").trim();
  if (details !== "reinstatement") {
    return details || "—";
  }

  if (!hearing.reinstatementNumber) {
    return details;
  }

  return `${details} (${hearing.reinstatementNumber})`;
};

const valueContainsSearch = (value: string | null | undefined, term: string) =>
  String(value || "").toLowerCase().includes(term);

const normalizeParticipantKey = (value: unknown) => String(value ?? "").trim();

const normalizeBooleanValue = (value: unknown) => {
  if (typeof value === "boolean") {
    return value;
  }
  return ["1", "true", "yes", "y"].includes(String(value ?? "").trim().toLowerCase());
};

const normalizeCaseNumbers = (caseNumbers: string | null | undefined, caseNumber: string | null | undefined) => {
  const values = String(caseNumbers || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const fallback = String(caseNumber || "").trim();
  if (fallback && !values.includes(fallback)) {
    values.push(fallback);
  }
  return values;
};

const buildHearingView = (hearing: ParticipantHearing, fallbackPrefix: string): HearingView => {
  const caseNumber = String(hearing.caseNumber || "").trim() || null;
  const caseNumbers = hearing.caseNumbers || caseNumber || null;
  const fallbackId = `${fallbackPrefix}-${caseNumber || "case"}-${hearing.hearingDate || "hearing"}-${hearing.id || "row"}`;
  const hearingId = String(hearing.hearingID || hearing.id || fallbackId);
  const filteredOrders = Array.isArray(hearing.courtOrders)
    ? hearing.courtOrders.map((order) => String(order || "").trim()).filter(Boolean)
    : [];

  return {
    participant: normalizeParticipantKey(hearing.participant) || null,
    hearingDate: hearing.hearingDate || null,
    hearingID: hearingId,
    caseNumber,
    caseNumbers,
    details: hearing.details || null,
    type: hearing.type || null,
    minutes: hearing.minutes || null,
    reinstatementNumber: hearing.reinstatementNumber || null,
    courtOrders: filteredOrders,
    rawHearing: hearing,
  };
};

export const LegalStatus = ({ odrNumber, programNumber }: { odrNumber: number; programNumber: number }) => {
  const [search, setSearch] = useState("");
  const [isEditLegalGeneralOpen, setIsEditLegalGeneralOpen] = useState(false);
  const [isAddBookingModalOpen, setIsAddBookingModalOpen] = useState(false);

  const [addCaseState, setAddCaseState] = useState<{ open: boolean; bookingNumber: string }>({
    open: false,
    bookingNumber: "",
  });
  const [addHearingState, setAddHearingState] = useState<{ open: boolean; bookingNumber: string; caseNumber: string }>({
    open: false,
    bookingNumber: "",
    caseNumber: "",
  });

  const [editBookingState, setEditBookingState] = useState<{ open: boolean; bookingData: any | null }>({
    open: false,
    bookingData: null,
  });
  const [deleteBookingState, setDeleteBookingState] = useState<{ open: boolean; bookingData: any | null }>({
    open: false,
    bookingData: null,
  });
  const [editCaseState, setEditCaseState] = useState<{ open: boolean; bookingNumber: string; caseData: any | null }>({
    open: false,
    bookingNumber: "",
    caseData: null,
  });
  const [deleteCaseState, setDeleteCaseState] = useState<{ open: boolean; caseData: any | null }>({
    open: false,
    caseData: null,
  });
  const [editHearingState, setEditHearingState] = useState<{ open: boolean; caseNumber: string; hearing: any | null }>({
    open: false,
    caseNumber: "",
    hearing: null,
  });
  const [deleteHearingState, setDeleteHearingState] = useState<{ open: boolean; hearing: any | null }>({
    open: false,
    hearing: null,
  });

	  const { loading, error, data, refetch } = useQuery(GET_LEGAL_STATUS_DATA, {
	    variables: { odrnumber: odrNumber, participant: programNumber },
	    fetchPolicy: "network-only",
	  });

  const legalData = data?.getParticipantByODRNumber as ParticipantLegalData | undefined;

  const allBookings = useMemo<BookingView[]>(() => {
    const rawBookings = legalData?.bookingDetails || [];

    const selectedParticipant = String(programNumber);

    return rawBookings
      .filter((booking) => {
        const bookingParticipant = normalizeParticipantKey(booking.participant);
        return !bookingParticipant || bookingParticipant === selectedParticipant;
      })
      .map((booking): BookingView => {
        const bookingNumber = String(booking.bookingNumber || "").trim();
        const cases = (booking.cases || [])
          .map((courtCase): CaseView => {
            const caseNumber = String(courtCase.caseNumber || "").trim();
            const hearings = (courtCase.hearings || [])
              .map((hearing): HearingView => buildHearingView(
                { ...hearing, caseNumber: hearing.caseNumber || caseNumber },
                `${bookingNumber || "booking"}-${caseNumber || "case"}`
              ))
              .sort((a, b) => {
                const aTime = normalizeDateValue(a.hearingDate) ? new Date(normalizeDateValue(a.hearingDate) as string).getTime() : 0;
                const bTime = normalizeDateValue(b.hearingDate) ? new Date(normalizeDateValue(b.hearingDate) as string).getTime() : 0;
                return bTime - aTime;
              });

            return {
              participant: normalizeParticipantKey(courtCase.participant) || selectedParticipant,
              caseNumber,
              entryDate: courtCase.entryDate || null,
              program: courtCase.program || null,
              caseOpen: courtCase.caseOpen === true || String(courtCase.caseOpen) === "1",
              caseOutcome: courtCase.caseOutcome || null,
              hideFromQuarterly: normalizeBooleanValue(courtCase.hideFromQuarterly),
              charges: Array.isArray(courtCase.charges) ? courtCase.charges : [],
              hearings,
              rawCase: courtCase,
            };
          })
          .sort((a, b) => {
            if (a.caseOpen !== b.caseOpen) {
              return a.caseOpen ? -1 : 1;
            }

            const aTime = normalizeDateValue(a.entryDate) ? new Date(normalizeDateValue(a.entryDate) as string).getTime() : 0;
            const bTime = normalizeDateValue(b.entryDate) ? new Date(normalizeDateValue(b.entryDate) as string).getTime() : 0;
            return bTime - aTime;
          });

        return {
          participant: normalizeParticipantKey(booking.participant) || selectedParticipant,
          program: booking.program || null,
          currentNumber: Number(booking.currentNumber || 0),
          bookingNumber,
          arrestDate: booking.arrestDate || null,
          releaseDate: booking.releaseDate || null,
          diversionDate: booking.diversionDate || null,
          location: booking.location || null,
          race: booking.race || null,
          cases,
        };
      })
      .sort((a, b) => {
        const aTime = normalizeDateValue(a.arrestDate) ? new Date(normalizeDateValue(a.arrestDate) as string).getTime() : 0;
        const bTime = normalizeDateValue(b.arrestDate) ? new Date(normalizeDateValue(b.arrestDate) as string).getTime() : 0;
        return bTime - aTime;
      });
  }, [legalData?.bookingDetails, programNumber]);

  const allCases = useMemo(() => allBookings.flatMap((booking) => booking.cases), [allBookings]);
  const allHearings = useMemo(() => allCases.flatMap((courtCase) => courtCase.hearings), [allCases]);
  const standaloneHearings = useMemo(() => {
    const selectedParticipant = String(programNumber);
    const nestedHearingIds = new Set(allHearings.map((hearing) => hearing.hearingID));
    const linkedCaseNumbers = new Set(allCases.map((courtCase) => courtCase.caseNumber).filter(Boolean));

    return (legalData?.hearings || [])
      .filter((hearing) => {
        const hearingParticipant = normalizeParticipantKey(hearing.participant);
        return !hearingParticipant || hearingParticipant === selectedParticipant;
      })
      .map((hearing) => buildHearingView(hearing, "standalone"))
      .filter((hearing) => {
        const hearingCaseNumbers = normalizeCaseNumbers(hearing.caseNumbers, hearing.caseNumber);
        const matchesKnownCase = hearingCaseNumbers.some((caseNumber) => linkedCaseNumbers.has(caseNumber));
        return !nestedHearingIds.has(hearing.hearingID) || !matchesKnownCase;
      })
      .sort((a, b) => {
        const aTime = normalizeDateValue(a.hearingDate) ? new Date(normalizeDateValue(a.hearingDate) as string).getTime() : 0;
        const bTime = normalizeDateValue(b.hearingDate) ? new Date(normalizeDateValue(b.hearingDate) as string).getTime() : 0;
        return bTime - aTime;
      });
  }, [allCases, allHearings, legalData?.hearings, programNumber]);
  const visibleHearings = useMemo(() => [...allHearings, ...standaloneHearings], [allHearings, standaloneHearings]);

  const totals = useMemo(() => {
    const openCases = allCases.filter((courtCase) => courtCase.caseOpen).length;
    const hearingsWithMinutes = visibleHearings.filter((hearing) => Boolean(getMinutesHref(hearing.minutes))).length;
    const futureHearings = visibleHearings
      .map((hearing) => ({ hearing, timestamp: normalizeDateValue(hearing.hearingDate) ? new Date(normalizeDateValue(hearing.hearingDate) as string).getTime() : Number.POSITIVE_INFINITY }))
      .filter((row) => Number.isFinite(row.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);

    return {
      bookingCount: allBookings.length,
      caseCount: allCases.length,
      openCases,
      closedCases: allCases.length - openCases,
      hearingCount: visibleHearings.length,
      hearingsWithMinutes,
      nextHearing: futureHearings[0]?.hearing || null,
    };
  }, [allBookings, allCases, visibleHearings]);

  const searchTerm = search.trim().toLowerCase();
  const filteredBookings = useMemo(() => {
    if (!searchTerm) {
      return allBookings;
    }

    return allBookings
      .map((booking) => {
        const bookingMatches =
          valueContainsSearch(booking.bookingNumber, searchTerm) ||
          valueContainsSearch(booking.location, searchTerm) ||
          valueContainsSearch(booking.race, searchTerm);

        const matchingCases = booking.cases
          .map((courtCase) => {
            const chargeText = courtCase.charges.map((charge) => String(charge.charge || "")).join(" ");
            const caseMatches =
              valueContainsSearch(courtCase.caseNumber, searchTerm) ||
              valueContainsSearch(courtCase.program, searchTerm) ||
              valueContainsSearch(courtCase.caseOutcome, searchTerm) ||
              valueContainsSearch(courtCase.caseOpen ? "open" : "closed", searchTerm) ||
              valueContainsSearch(chargeText, searchTerm);

            const matchingHearings = courtCase.hearings.filter((hearing) => {
              const orderText = hearing.courtOrders.join(" ");
              return (
                valueContainsSearch(hearing.type, searchTerm) ||
                valueContainsSearch(hearing.details, searchTerm) ||
                valueContainsSearch(hearing.hearingDate, searchTerm) ||
                valueContainsSearch(orderText, searchTerm)
              );
            });

            if (!bookingMatches && !caseMatches && matchingHearings.length === 0) {
              return null;
            }

            if (bookingMatches || caseMatches) {
              return courtCase;
            }

            return {
              ...courtCase,
              hearings: matchingHearings,
            };
          })
          .filter(Boolean) as CaseView[];

        if (!bookingMatches && matchingCases.length === 0) {
          return null;
        }

        if (bookingMatches) {
          return booking;
        }

        return {
          ...booking,
          cases: matchingCases,
        };
      })
      .filter(Boolean) as BookingView[];
  }, [allBookings, searchTerm]);

  if (loading) {
    return <TabSpinner label="Loading legal status..." />;
  }

  if (error || !legalData) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="flex items-center gap-3 p-6 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">Error loading legal status data. Please try again.</span>
        </CardContent>
      </Card>
    );
  }

  const currentBooking = allBookings.find((booking) => booking.currentNumber === 1) || allBookings[0] || null;
  const istDisplay = [
    legalData.istStatus?.status,
    formatDate(legalData.istStatus?.statusDate),
  ].filter((value) => value && value !== "—").join(" ");

  const snapshotItems = [
    {
      icon: FolderOpen,
      label: "Current Booking",
      value: currentBooking?.bookingNumber || legalData.bookingNumber || "No current booking",
      tone: currentBooking?.bookingNumber || legalData.bookingNumber
        ? "border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)] text-[var(--neutral-gray-120)]"
        : "border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] text-[var(--neutral-gray-90)]",
    },
    {
      icon: FileText,
      label: "MAIN Last 4",
      value: legalData.mainNumber || "—",
      tone: "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
    {
      icon: CalendarDays,
      label: "Arrest Date",
      value: formatDate(currentBooking?.arrestDate || legalData.arrestDate),
      tone: "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
    {
      icon: CalendarDays,
      label: "Projected Release",
      value: formatDate(currentBooking?.releaseDate || legalData.releasedDate),
      tone: "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
    {
      icon: CalendarDays,
      label: "Max Date",
      value: formatDate(legalData.maxDate),
      tone: "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
    {
      icon: FileText,
      label: "IST Status",
      value: istDisplay || "—",
      tone: "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
    {
      icon: Shield,
      label: "RSO",
      value: normalizeRso(legalData.rso) ? "Yes" : "No",
      tone: normalizeRso(legalData.rso)
        ? "border-[var(--semantic-warning)]/25 bg-[var(--semantic-warning)]/10 text-[var(--neutral-gray-120)]"
        : "border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-120)]",
    },
  ];

  return (
    <div className="space-y-4 p-1">
      <TabHeader
        icon={Scale}
        title="Legal Status"
        subtitle="Bookings, cases, hearings, court orders, and minutes."
        action={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-3 text-[var(--neutral-gray-110)] hover:bg-[var(--surface-muted)]"
              onClick={() => setIsEditLegalGeneralOpen(true)}
            >
              <Edit className="mr-1.5 h-3.5 w-3.5" />
              Edit Legal General
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-[var(--azure-primary)] bg-[var(--azure-primary)] px-4 text-white hover:bg-[var(--azure-primary-hover)] hover:text-white"
              onClick={() => setIsAddBookingModalOpen(true)}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add Booking
            </Button>
          </>
        }
      />

      <TabToolbar
        left={
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--neutral-gray-80)]" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search booking, case, hearing, order, or charge..."
              className="h-8 w-full bg-[var(--surface-panel-solid)] pl-8 text-sm focus-visible:ring-1 sm:w-96"
            />
          </div>
        }
        right={
          <>
            <Badge variant="outline" className="h-6 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2.5 text-xs font-medium text-[var(--neutral-gray-100)]">
              Bookings: {totals.bookingCount}
            </Badge>
            <Badge variant="outline" className="h-6 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2.5 text-xs font-medium text-[var(--neutral-gray-100)]">
              Cases: {totals.caseCount}
            </Badge>
            <Badge variant="outline" className="h-6 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2.5 text-xs font-medium text-[var(--neutral-gray-100)]">
              Hearings: {totals.hearingCount}
            </Badge>
          </>
        }
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.95fr)]">
        <Card className={sectionCardClassName}>
          <CardContent className="p-4 md:p-5">
            <div className="participant-card-header mb-0">
              <div>
                <div className="participant-card-title">General Legal Information</div>
                <div className="participant-card-subtitle">Current legal banner fields for the selected program participant.</div>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {snapshotItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className={`rounded-[var(--border-radius-medium)] border px-3 py-3 ${item.tone}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
                        <Icon className="h-4 w-4 text-[var(--azure-primary)]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">{item.label}</div>
                        <div className="mt-1 break-words text-sm font-medium text-[var(--neutral-gray-120)]">{item.value}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className={sectionCardClassName}>
          <CardContent className="p-4 md:p-5">
            <div className="participant-card-header mb-0">
              <div>
                <div className="participant-card-title">Legal Snapshot</div>
                <div className="participant-card-subtitle">Open/closed case mix and hearing readiness.</div>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-3 py-3">
                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">Case Status</div>
                <div className="mt-1 text-sm font-medium text-[var(--neutral-gray-120)]">
                  {totals.openCases} open / {totals.closedCases} closed
                </div>
              </div>
              <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-3 py-3">
                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">Minutes Uploaded</div>
                <div className="mt-1 text-sm font-medium text-[var(--neutral-gray-120)]">
                  {totals.hearingsWithMinutes} of {totals.hearingCount}
                </div>
              </div>
              <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-3 py-3">
                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">Next Hearing</div>
                <div className="mt-1 text-sm font-medium text-[var(--neutral-gray-120)]">
                  {totals.nextHearing ? formatDate(totals.nextHearing.hearingDate) : "No hearing on file"}
                </div>
                <div className="mt-1 text-sm text-[var(--neutral-gray-90)]">
                  {totals.nextHearing ? formatHearingDetails(totals.nextHearing) : "Add a case hearing to begin tracking."}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className={sectionCardClassName}>
        <CardContent className="p-0">
          <div className="border-b border-[var(--neutral-gray-30)] px-4 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="participant-card-title">Bookings, Cases, and Hearings</div>
                <div className="participant-card-subtitle">Structured legal workflow grouped by booking number.</div>
              </div>
              <Badge variant="outline" className="h-6 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2.5 text-xs font-medium text-[var(--neutral-gray-100)]">
                Showing {filteredBookings.length} of {allBookings.length} bookings
              </Badge>
            </div>
          </div>

          {!filteredBookings.length ? (
            <div className="p-8 text-center text-sm text-[var(--neutral-gray-90)]">
              No legal records match this search.
            </div>
          ) : (
            <Accordion type="multiple" className="w-full">
              {filteredBookings.map((booking) => (
                <AccordionItem key={booking.bookingNumber || "no-booking"} value={`booking-${booking.bookingNumber || "no-booking"}`} className="border-b border-[var(--neutral-gray-30)]">
                  <div className="px-4" data-testid={`legal-booking-${booking.bookingNumber || "no-booking"}`}>
                    <AccordionTrigger className="py-3 hover:no-underline">
                      <div className="flex w-full flex-wrap items-center justify-between gap-3 pr-2 text-left">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                            Booking {booking.bookingNumber || "—"}
                          </div>
                          <div className="text-xs text-[var(--neutral-gray-90)]">
                            Arrest {formatDate(booking.arrestDate)} • Release {formatDate(booking.releaseDate)}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-100)]">
                            {booking.cases.length} cases
                          </Badge>
                          <Badge variant="outline" className="border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] text-[var(--neutral-gray-100)]">
                            {booking.cases.reduce((sum, courtCase) => sum + courtCase.hearings.length, 0)} hearings
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                  </div>

                  <AccordionContent className="px-4 pb-4">
                    <div className="mb-3 flex flex-wrap justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className={inlineActionClassName}
                        onClick={() => setAddCaseState({ open: true, bookingNumber: booking.bookingNumber })}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add Case
                      </Button>
                      <Button
                        aria-label={`Edit booking ${booking.bookingNumber}`}
                        data-testid={`legal-booking-edit-${booking.bookingNumber}`}
                        variant="outline"
                        size="sm"
                        className={iconButtonClassName}
                        title="Edit booking"
                        onClick={() =>
                          setEditBookingState({
                            open: true,
                            bookingData: {
                              bookingNumber: booking.bookingNumber,
                              arrestDate: booking.arrestDate,
                              releaseDate: booking.releaseDate,
                            },
                          })
                        }
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        aria-label={`Delete booking ${booking.bookingNumber}`}
                        data-testid={`legal-booking-delete-${booking.bookingNumber}`}
                        variant="outline"
                        size="sm"
                        className={`${iconButtonClassName} hover:text-[var(--semantic-error)]`}
                        title="Delete booking"
                        onClick={() => setDeleteBookingState({ open: true, bookingData: { bookingNumber: booking.bookingNumber } })}
                      >
                        <Trash className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    {!booking.cases.length ? (
                      <div className="rounded-[var(--border-radius-medium)] border border-dashed border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-4 py-6 text-center text-sm text-[var(--neutral-gray-90)]">
                        No cases linked to this booking yet.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {booking.cases.map((courtCase) => (
                          <div key={`${booking.bookingNumber}-${courtCase.caseNumber}`} className="overflow-hidden rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--neutral-gray-30)] p-3">
                              <div>
                                <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                                  Case {courtCase.caseNumber || "—"}
                                </div>
	                                <div className="mt-1 text-xs text-[var(--neutral-gray-90)]">
	                                  {courtCase.program || "No program"} • Entered {formatDate(courtCase.entryDate)}
	                                </div>
	                                {(courtCase.caseOutcome || courtCase.hideFromQuarterly) && (
	                                  <div className="mt-1 text-xs text-[var(--neutral-gray-90)]">
	                                    {courtCase.caseOutcome ? `Outcome ${courtCase.caseOutcome}` : ""}
	                                    {courtCase.caseOutcome && courtCase.hideFromQuarterly ? " • " : ""}
	                                    {courtCase.hideFromQuarterly ? "Hidden from quarterly" : ""}
	                                  </div>
	                                )}
	                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={
                                    courtCase.caseOpen
                                      ? "border-[var(--azure-primary-border-soft)] bg-[var(--azure-primary-soft)] text-[var(--azure-primary)]"
                                      : "border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] text-[var(--neutral-gray-100)]"
                                  }
                                >
                                  {courtCase.caseOpen ? "Open" : "Closed"}
                                </Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={inlineActionClassName}
                                  onClick={() =>
                                    setAddHearingState({
                                      open: true,
                                      bookingNumber: booking.bookingNumber,
                                      caseNumber: courtCase.caseNumber,
                                    })
                                  }
                                >
                                  <Plus className="mr-1 h-3.5 w-3.5" />
                                  Add Hearing
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={iconButtonClassName}
                                  title="Edit case"
                                  onClick={() =>
                                    setEditCaseState({
                                      open: true,
                                      bookingNumber: booking.bookingNumber,
                                      caseData: courtCase.rawCase,
                                    })
                                  }
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={`${iconButtonClassName} hover:text-[var(--semantic-error)]`}
                                  title="Delete case"
                                  onClick={() => setDeleteCaseState({ open: true, caseData: courtCase.rawCase })}
                                >
                                  <Trash className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>

                            <div className="grid gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                              <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-3">
                                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">Charges</div>
                                {!courtCase.charges.length ? (
                                  <div className="mt-2 text-sm text-[var(--neutral-gray-90)]">No charges listed.</div>
                                ) : (
                                  <div className="mt-2 space-y-1.5">
                                    {courtCase.charges.map((charge, index) => (
                                      <div key={`${courtCase.caseNumber}-charge-${index}`} className="text-sm text-[var(--neutral-gray-110)]">
                                        {charge.charge || "—"}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] p-3">
                                <div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--neutral-gray-80)]">Hearings</div>
                                {!courtCase.hearings.length ? (
                                  <div className="text-sm text-[var(--neutral-gray-90)]">No hearings added.</div>
                                ) : (
                                  <div className="space-y-2">
                                    {courtCase.hearings.map((hearing) => {
                                      const minutesHref = getMinutesHref(hearing.minutes);
                                      return (
                                        <div key={`${courtCase.caseNumber}-${hearing.hearingID}`} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] p-3">
                                          <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div>
                                              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                                                {formatDate(hearing.hearingDate)}
                                              </div>
                                              <div className="mt-0.5 text-xs text-[var(--neutral-gray-90)]">
                                                {hearing.type || "Unspecified type"}
                                              </div>
                                            </div>
                                            <div className="flex gap-1">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className={iconButtonClassName}
                                                title="Edit hearing"
                                                onClick={() =>
                                                  setEditHearingState({
                                                    open: true,
                                                    caseNumber: courtCase.caseNumber,
                                                    hearing: hearing.rawHearing,
                                                  })
                                                }
                                              >
                                                <Edit className="h-3.5 w-3.5" />
                                              </Button>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className={`${iconButtonClassName} hover:text-[var(--semantic-error)]`}
                                                title="Delete hearing"
                                                onClick={() => setDeleteHearingState({ open: true, hearing: hearing.rawHearing })}
                                              >
                                                <Trash className="h-3.5 w-3.5" />
                                              </Button>
                                            </div>
                                          </div>

                                          <div className="mt-2 text-sm text-[var(--neutral-gray-110)]">
                                            {formatHearingDetails(hearing)}
                                          </div>
                                          <div className="mt-2 text-xs text-[var(--neutral-gray-90)]">
                                            Orders: {hearing.courtOrders.length ? hearing.courtOrders.join(", ") : "—"}
                                          </div>
                                          <div className="mt-1 text-xs text-[var(--neutral-gray-90)]">
                                            Minutes:{" "}
                                            {minutesHref ? (
                                              <a
                                                href={minutesHref}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="font-medium text-[var(--azure-primary)] hover:text-[var(--azure-primary-hover)] hover:underline"
                                              >
                                                Open
                                              </a>
                                            ) : (
                                              "No minutes uploaded"
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {standaloneHearings.length > 0 && (
        <Card className={sectionCardClassName}>
          <CardContent className="p-4 md:p-5">
            <div className="participant-card-header mb-0">
              <div>
                <div className="participant-card-title">Standalone Hearings</div>
                <div className="participant-card-subtitle">Hearings on file that are not attached to a displayed canonical case.</div>
              </div>
              <Badge variant="outline" className="h-6 border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] px-2.5 text-xs font-medium text-[var(--neutral-gray-100)]">
                {standaloneHearings.length} hearings
              </Badge>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {standaloneHearings.map((hearing) => {
                const minutesHref = getMinutesHref(hearing.minutes);
                const editableCaseNumber = normalizeCaseNumbers(hearing.caseNumbers, hearing.caseNumber)[0] || "";
                return (
                  <div key={`standalone-${hearing.hearingID}`} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)] p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">{formatDate(hearing.hearingDate)}</div>
                        <div className="mt-0.5 text-xs text-[var(--neutral-gray-90)]">
                          {hearing.type || "Unspecified type"} {editableCaseNumber ? `• Case ${editableCaseNumber}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {editableCaseNumber && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={iconButtonClassName}
                            title="Edit hearing"
                            onClick={() => setEditHearingState({ open: true, caseNumber: editableCaseNumber, hearing: hearing.rawHearing })}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className={`${iconButtonClassName} hover:text-[var(--semantic-error)]`}
                          title="Delete hearing"
                          onClick={() => setDeleteHearingState({ open: true, hearing: hearing.rawHearing })}
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-[var(--neutral-gray-110)]">{formatHearingDetails(hearing)}</div>
                    <div className="mt-2 text-xs text-[var(--neutral-gray-90)]">
                      Orders: {hearing.courtOrders.length ? hearing.courtOrders.join(", ") : "—"}
                    </div>
                    <div className="mt-1 text-xs text-[var(--neutral-gray-90)]">
                      Minutes:{" "}
                      {minutesHref ? (
                        <a href={minutesHref} target="_blank" rel="noreferrer" className="font-medium text-[var(--azure-primary)] hover:text-[var(--azure-primary-hover)] hover:underline">
                          Open
                        </a>
                      ) : (
                        "No minutes uploaded"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <AddBookingModal
        isOpen={isAddBookingModalOpen}
        onClose={() => setIsAddBookingModalOpen(false)}
        odrNumber={odrNumber}
        refetch={() => refetch()}
        selectedProgram={programNumber.toString()}
      />

      <AddCaseModal
        isOpen={addCaseState.open}
        onClose={() => setAddCaseState({ open: false, bookingNumber: "" })}
        odrNumber={odrNumber}
        bookingNumber={addCaseState.bookingNumber}
        participant={programNumber}
        refetch={() => refetch()}
      />

      <AddHearingModal
        isOpen={addHearingState.open}
        onClose={() => setAddHearingState({ open: false, bookingNumber: "", caseNumber: "" })}
        odrNumber={odrNumber}
        bookingNumber={addHearingState.bookingNumber}
        caseNumber={addHearingState.caseNumber}
        participant={programNumber}
        refetch={() => refetch()}
      />

      <EditBookingModal
        isOpen={editBookingState.open}
        onClose={() => setEditBookingState({ open: false, bookingData: null })}
        odrNumber={odrNumber}
        selectedProgram={programNumber.toString()}
        bookingData={editBookingState.bookingData}
        refetch={refetch}
      />

      <DeleteBookingModal
        isOpen={deleteBookingState.open}
        onClose={() => setDeleteBookingState({ open: false, bookingData: null })}
        odrNumber={odrNumber}
        selectedProgram={programNumber.toString()}
        bookingData={deleteBookingState.bookingData}
        refetch={refetch}
      />

      <EditCaseModal
        isOpen={editCaseState.open}
        onClose={() => setEditCaseState({ open: false, bookingNumber: "", caseData: null })}
        odrNumber={odrNumber}
        participant={programNumber}
        bookingNumber={editCaseState.bookingNumber}
        caseData={editCaseState.caseData}
        refetch={refetch}
      />

      <DeleteCaseModal
        isOpen={deleteCaseState.open}
        onClose={() => setDeleteCaseState({ open: false, caseData: null })}
        odrNumber={odrNumber}
        participant={programNumber}
        caseData={deleteCaseState.caseData}
        refetch={refetch}
      />

      <EditHearingModal
        isOpen={editHearingState.open}
        onClose={() => setEditHearingState({ open: false, caseNumber: "", hearing: null })}
        odrNumber={odrNumber}
        participant={programNumber}
        caseNumber={editHearingState.caseNumber}
        hearing={editHearingState.hearing}
        refetch={refetch}
      />

      <DeleteHearingModal
        isOpen={deleteHearingState.open}
        onClose={() => setDeleteHearingState({ open: false, hearing: null })}
        odrNumber={odrNumber}
        participant={programNumber}
        hearing={deleteHearingState.hearing}
        refetch={refetch}
      />

      <EditLegalGeneral
        isOpen={isEditLegalGeneralOpen}
        onClose={() => setIsEditLegalGeneralOpen(false)}
        odrNumber={odrNumber}
        participant={programNumber}
        legalData={legalData}
        refetch={refetch}
      />
    </div>
  );
};
